import React, { useEffect, useMemo, useState } from "react";
import { getAdminCourses } from "../../../api/coursesAdmin";
import {
  deleteAdminTraining,
  getAdminTrainings,
  removeAdminTrainingTrainerAvatar,
  saveAdminTrainingAllLanguages,
  uploadAdminTrainingTrainerAvatar,
} from "../../../api/trainingsAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";
import AdminEditorModal from "./AdminEditorModal";
import AdminCollapsibleSection from "./AdminCollapsibleSection";
import AdminProductListCard from "./AdminProductListCard";
import { DashboardReloadButton } from "./dashboardUi";
import {
  btnAccent,
  btnDanger,
  btnPrimary,
  btnSecondary,
  langTabActive,
  langTabIdle,
} from "./adminProductUi";

const LANGS = ["en", "ar", "ku"];
const LANG_LABELS = { en: "English", ar: "Arabic", ku: "Kurdish" };

const normLang = (value) =>
  String(value || "en")
    .toLowerCase()
    .split("-")[0];

const normKey = (value) => String(value || "").trim().toLowerCase();

const emptyLangFields = () => ({
  title: "",
  description: "",
  benefits: [{ text: "" }],
  registerNowText: "Register now",
  downloadBrochureText: "Download brochure",
  trainerSectionTitle: "Meet the trainer",
  trainerRole: "Trainer",
  trainerName: "",
  trainerBio: "",
  trainerHighlights: [{ text: "" }],
  courseInfoTitle: "Course information",
  courseInfoSubtitle: "",
  dateText: "",
  locationText: "",
  formatText: "",
  languageText: "",
  seatsRemainingText: "",
  contactText: "",
});

const emptyTranslations = () => ({
  en: emptyLangFields(),
  ar: emptyLangFields(),
  ku: emptyLangFields(),
});

const EMPTY_FORM = {
  courseKey: "",
  displayOrder: 1,
  isActive: true,
  trainerAvatarImageUrl: "",
  translations: emptyTranslations(),
  ids: {},
  courseIds: {},
  primaryId: null,
  primaryCourseId: null,
};

const mapRowToLangFields = (training) => ({
  title: training?.title || "",
  description: training?.description || "",
  benefits:
    Array.isArray(training?.benefits) && training.benefits.length
      ? training.benefits.map((x) => ({ text: x?.text || "" }))
      : [{ text: "" }],
  registerNowText: training?.registerNowText || "Register now",
  downloadBrochureText: training?.downloadBrochureText || "Download brochure",
  trainerSectionTitle: training?.trainerSectionTitle || "Meet the trainer",
  trainerRole: training?.trainerRole || "Trainer",
  trainerName: training?.trainerName || "",
  trainerBio: training?.trainerBio || "",
  trainerHighlights:
    Array.isArray(training?.trainerHighlights) && training.trainerHighlights.length
      ? training.trainerHighlights.map((x) => ({ text: x?.text || "" }))
      : [{ text: "" }],
  courseInfoTitle: training?.courseInfoTitle || "Course information",
  courseInfoSubtitle: training?.courseInfoSubtitle || "",
  dateText: training?.dateText || "",
  locationText: training?.locationText || "",
  formatText: training?.formatText || "",
  languageText: training?.languageText || "",
  seatsRemainingText: training?.seatsRemainingText || "",
  contactText: training?.contactText || "",
});

const mapRowsToForm = (rows, courseKey = "") => {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const primary = list.find((r) => normLang(r?.language) === "en") || list[0] || {};
  const translations = emptyTranslations();
  const ids = {};
  const courseIds = {};
  list.forEach((row) => {
    const lang = normLang(row?.language);
    if (!LANGS.includes(lang)) return;
    translations[lang] = mapRowToLangFields(row);
    ids[lang] = row?.id ?? null;
    courseIds[lang] = row?.courseId ?? null;
  });
  return {
    courseKey: courseKey || primary?.courseKey || "",
    displayOrder: Number(primary?.displayOrder || 1),
    isActive: list.length ? list.every((r) => r?.isActive !== false) : true,
    trainerAvatarImageUrl:
      primary?.trainerAvatarImageUrl ||
      list.find((r) => r?.trainerAvatarImageUrl)?.trainerAvatarImageUrl ||
      "",
    translations,
    ids,
    courseIds,
    primaryId: primary?.id ?? list[0]?.id ?? null,
    primaryCourseId: primary?.courseId ?? list[0]?.courseId ?? null,
  };
};

const toLanguageContent = (lang, fields) => ({
  language: lang,
  title: String(fields.title || "").trim(),
  description: String(fields.description || "").trim(),
  benefits: (fields.benefits || [])
    .map((x) => ({ text: String(x?.text || "").trim() }))
    .filter((x) => x.text),
  registerNowText: String(fields.registerNowText || "").trim(),
  downloadBrochureText: String(fields.downloadBrochureText || "").trim(),
  trainerSectionTitle: String(fields.trainerSectionTitle || "").trim(),
  trainerRole: String(fields.trainerRole || "").trim(),
  trainerName: String(fields.trainerName || "").trim(),
  trainerBio: String(fields.trainerBio || "").trim(),
  trainerHighlights: (fields.trainerHighlights || [])
    .map((x) => ({ text: String(x?.text || "").trim() }))
    .filter((x) => x.text),
  courseInfoTitle: String(fields.courseInfoTitle || "").trim(),
  courseInfoSubtitle: String(fields.courseInfoSubtitle || "").trim(),
  dateText: String(fields.dateText || "").trim(),
  locationText: String(fields.locationText || "").trim(),
  formatText: String(fields.formatText || "").trim(),
  languageText: String(fields.languageText || "").trim(),
  seatsRemainingText: String(fields.seatsRemainingText || "").trim(),
  contactText: String(fields.contactText || "").trim(),
});

const buildSavePayload = (form) => ({
  displayOrder: Number(form.displayOrder || 1),
  isActive: Boolean(form.isActive),
  trainerAvatarImageUrl: String(form.trainerAvatarImageUrl || "").trim() || null,
  languages: LANGS.filter((lang) => (form.translations?.[lang]?.title || "").trim()).map((lang) =>
    toLanguageContent(lang, form.translations[lang])
  ),
});

const apiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message || "";
  if (typeof data === "string") return data;
  if (data.message) return data.message;
  if (data.title && !data.errors) return data.title;
  if (data.errors && typeof data.errors === "object") {
    return Object.entries(data.errors)
      .flatMap(([key, msgs]) => (Array.isArray(msgs) ? msgs : [msgs]).map((m) => `${key}: ${m}`))
      .join(" · ");
  }
  return error?.message || "";
};

const TrainingsManager = () => {
  const { notify } = useNotification();
  const [courses, setCourses] = useState([]);
  const [allTrainings, setAllTrainings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [createAvatarFile, setCreateAvatarFile] = useState(null);
  const [activeCreateLang, setActiveCreateLang] = useState("en");
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editAvatarFile, setEditAvatarFile] = useState(null);
  const [activeEditLang, setActiveEditLang] = useState("en");
  const [saving, setSaving] = useState(false);

  const courseGroups = useMemo(() => {
    const map = new Map();
    courses.forEach((course) => {
      const key = normKey(course?.courseKey);
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(course);
    });
    return Array.from(map.entries())
      .map(([key, rows]) => {
        const en = rows.find((r) => normLang(r.language) === "en");
        const title = en?.title || rows[0]?.title || rows[0]?.courseKey || key;
        const courseKey = en?.courseKey || rows[0]?.courseKey || key;
        const langs = LANGS.filter((lang) => rows.some((r) => normLang(r.language) === lang));
        const anchor = en || rows[0];
        return {
          key,
          courseKey,
          title,
          langs,
          anchorId: anchor?.id,
          rows,
        };
      })
      .sort((a, b) => String(a.title).localeCompare(String(b.title)));
  }, [courses]);

  const resolveCourseGroup = (courseKey, courseList = courses) => {
    const key = normKey(courseKey);
    if (!key) return null;
    const rows = (Array.isArray(courseList) ? courseList : []).filter(
      (c) => normKey(c?.courseKey) === key
    );
    if (!rows.length) return null;
    const en = rows.find((r) => normLang(r.language) === "en");
    const anchor = en || rows[0];
    return {
      key,
      courseKey: anchor?.courseKey || courseKey,
      title: en?.title || rows[0]?.title || courseKey,
      langs: LANGS.filter((lang) => rows.some((r) => normLang(r.language) === lang)),
      anchorId: anchor?.id,
      rows,
    };
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const courseData = await getAdminCourses();
      const courseList = Array.isArray(courseData) ? courseData : [];
      setCourses(courseList);

      // One admin fetch per course row (EN/AR/KU), then flatten.
      const lists = await Promise.all(
        courseList.map((course) =>
          getAdminTrainings({ courseId: course.id }).catch(() => [])
        )
      );

      const flat = lists.flatMap((list, idx) => {
        const course = courseList[idx];
        return (Array.isArray(list) ? list : []).map((row) => ({
          ...row,
          language: normLang(row?.language || course?.language),
          courseKey: row?.courseKey || course?.courseKey || "",
          courseId: row?.courseId || course?.id,
        }));
      });
      setAllTrainings(flat);

      setCreateForm((prev) => {
        if (prev.courseKey) return prev;
        const preferred =
          courseList.find((c) => normLang(c.language) === "en") || courseList[0];
        return preferred?.courseKey
          ? { ...prev, courseKey: preferred.courseKey }
          : prev;
      });
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load trainings",
        message: apiErrorMessage(error),
      });
      setAllTrainings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedTrainings = useMemo(() => {
    const map = new Map();
    allTrainings.forEach((row) => {
      const courseKey = row?.courseKey || "";
      const order = Number(row?.displayOrder || 0);
      const groupKey = `${normKey(courseKey)}::${order}`;
      if (!map.has(groupKey)) map.set(groupKey, []);
      map.get(groupKey).push(row);
    });

    return Array.from(map.entries())
      .map(([groupKey, rows]) => {
        const courseKey = rows[0]?.courseKey || "";
        const displayOrder = Number(rows[0]?.displayOrder || 0);
        const form = mapRowsToForm(rows, courseKey);
        const courseGroup = resolveCourseGroup(courseKey);
        const primary = rows.find((r) => normLang(r.language) === "en") || rows[0];
        return {
          groupKey,
          courseKey,
          courseTitle: courseGroup?.title || courseKey,
          displayOrder,
          rows,
          form,
          title: form.translations.en?.title || primary?.title || `Training #${displayOrder}`,
          isActive: form.isActive,
          avatar: form.trainerAvatarImageUrl,
          languages: LANGS.filter((lang) => rows.some((r) => normLang(r.language) === lang)),
          primaryId: form.primaryId,
          primaryCourseId: form.primaryCourseId || courseGroup?.anchorId,
        };
      })
      .sort((a, b) => {
        const byCourse = String(a.courseTitle).localeCompare(String(b.courseTitle));
        if (byCourse !== 0) return byCourse;
        return a.displayOrder - b.displayOrder;
      });
  }, [allTrainings, courses]);

  const nextDisplayOrderForCourse = (courseKey) => {
    const key = normKey(courseKey);
    const max = groupedTrainings
      .filter((g) => normKey(g.courseKey) === key)
      .reduce((acc, g) => Math.max(acc, Number(g.displayOrder || 0)), 0);
    return max + 1;
  };

  const setLangField = (setForm, lang, partial) => {
    setForm((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: { ...prev.translations[lang], ...partial },
      },
    }));
  };

  const setLangList = (setForm, lang, key, next) => {
    setForm((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: { ...prev.translations[lang], [key]: next },
      },
    }));
  };

  const renderTextList = (rows, setRows, addLabel = "Add item") => (
    <div className="rounded-xl border border-slate-200 p-3 sm:col-span-2">
      {rows.map((row, idx) => (
        <div key={idx} className="flex gap-2 mb-2">
          <input
            type="text"
            value={row?.text || ""}
            onChange={(e) => {
              const next = [...rows];
              next[idx] = { ...(next[idx] || {}), text: e.target.value };
              setRows(next);
            }}
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => {
              const next = rows.filter((_, i) => i !== idx);
              setRows(next.length ? next : [{ text: "" }]);
            }}
            className="px-3 rounded border border-rose-200 text-rose-700"
          >
            -
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setRows([...(rows || []), { text: "" }])}
        className="text-xs px-3 py-1.5 rounded border border-slate-300"
      >
        {addLabel}
      </button>
    </div>
  );

  const renderLangTabs = (activeLang, setActiveLang, form, keyPrefix) => (
    <div className="flex flex-wrap items-center gap-2">
      {LANGS.map((lang) => {
        const hasContent = Boolean((form.translations?.[lang]?.title || "").trim());
        return (
          <button
            key={`${keyPrefix}-${lang}`}
            type="button"
            onClick={() => setActiveLang(lang)}
            className={activeLang === lang ? langTabActive : langTabIdle}
          >
            {LANG_LABELS[lang]}
            {hasContent ? " ●" : ""}
          </button>
        );
      })}
    </div>
  );

  const renderLangFields = (form, setForm, activeLang) => {
    const fields = form.translations[activeLang] || emptyLangFields();
    return (
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-sm text-slate-700 sm:col-span-2">
          Title ({LANG_LABELS[activeLang]})
          <input
            type="text"
            value={fields.title}
            onChange={(e) => setLangField(setForm, activeLang, { title: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700 sm:col-span-2">
          Description
          <textarea
            value={fields.description}
            onChange={(e) => setLangField(setForm, activeLang, { description: e.target.value })}
            className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        {renderTextList(
          fields.benefits,
          (next) => setLangList(setForm, activeLang, "benefits", next),
          "Add benefit"
        )}
        {renderTextList(
          fields.trainerHighlights,
          (next) => setLangList(setForm, activeLang, "trainerHighlights", next),
          "Add trainer highlight"
        )}
        <label className="text-sm text-slate-700">
          Register Now Text
          <input
            type="text"
            value={fields.registerNowText}
            onChange={(e) => setLangField(setForm, activeLang, { registerNowText: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Download Brochure Text
          <input
            type="text"
            value={fields.downloadBrochureText}
            onChange={(e) =>
              setLangField(setForm, activeLang, { downloadBrochureText: e.target.value })
            }
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Trainer Section Title
          <input
            type="text"
            value={fields.trainerSectionTitle}
            onChange={(e) =>
              setLangField(setForm, activeLang, { trainerSectionTitle: e.target.value })
            }
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Trainer Role
          <input
            type="text"
            value={fields.trainerRole}
            onChange={(e) => setLangField(setForm, activeLang, { trainerRole: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Trainer Name
          <input
            type="text"
            value={fields.trainerName}
            onChange={(e) => setLangField(setForm, activeLang, { trainerName: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700 sm:col-span-2">
          Trainer Bio
          <textarea
            value={fields.trainerBio}
            onChange={(e) => setLangField(setForm, activeLang, { trainerBio: e.target.value })}
            className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Course Info Title
          <input
            type="text"
            value={fields.courseInfoTitle}
            onChange={(e) => setLangField(setForm, activeLang, { courseInfoTitle: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Course Info Subtitle
          <input
            type="text"
            value={fields.courseInfoSubtitle}
            onChange={(e) =>
              setLangField(setForm, activeLang, { courseInfoSubtitle: e.target.value })
            }
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Date Text
          <input
            type="text"
            value={fields.dateText}
            onChange={(e) => setLangField(setForm, activeLang, { dateText: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Location Text
          <input
            type="text"
            value={fields.locationText}
            onChange={(e) => setLangField(setForm, activeLang, { locationText: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Format Text
          <input
            type="text"
            value={fields.formatText}
            onChange={(e) => setLangField(setForm, activeLang, { formatText: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Language Text
          <input
            type="text"
            value={fields.languageText}
            onChange={(e) => setLangField(setForm, activeLang, { languageText: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Seats Remaining Text
          <input
            type="text"
            value={fields.seatsRemainingText}
            onChange={(e) =>
              setLangField(setForm, activeLang, { seatsRemainingText: e.target.value })
            }
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Contact Text
          <input
            type="text"
            value={fields.contactText}
            onChange={(e) => setLangField(setForm, activeLang, { contactText: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
      </div>
    );
  };

  const closeEdit = () => {
    setEditingKey(null);
    setEditForm(EMPTY_FORM);
    setEditAvatarFile(null);
    setActiveEditLang("en");
  };

  const handleCreate = async () => {
    const courseGroup = resolveCourseGroup(createForm.courseKey);
    if (!courseGroup?.anchorId) {
      return notify({
        type: "error",
        title: "Select a course",
        message: "Create a course first under Courses, then pick it here.",
      });
    }
    const titledLangs = LANGS.filter((lang) => (createForm.translations[lang].title || "").trim());
    if (!titledLangs.length) {
      return notify({ type: "error", title: "Training title is required in at least one language" });
    }

    const displayOrder = Number(createForm.displayOrder || nextDisplayOrderForCourse(courseGroup.courseKey));
    const payload = buildSavePayload({ ...createForm, displayOrder });
    if (!payload.languages.length) {
      return notify({ type: "error", title: "Add a title for at least one language" });
    }

    setCreating(true);
    try {
      const saved = await saveAdminTrainingAllLanguages({
        courseId: courseGroup.anchorId,
        payload,
      });
      const primary =
        (Array.isArray(saved) ? saved : []).find((r) => normLang(r.language) === "en") ||
        (Array.isArray(saved) ? saved[0] : null);
      if (createAvatarFile && primary?.id && primary?.courseId) {
        await uploadAdminTrainingTrainerAvatar({
          courseId: primary.courseId,
          trainingId: primary.id,
          file: createAvatarFile,
        });
      }
      notify({ type: "success", title: "Training saved for all languages" });
      setCreateForm({
        ...EMPTY_FORM,
        courseKey: courseGroup.courseKey,
        translations: emptyTranslations(),
      });
      setCreateAvatarFile(null);
      setActiveCreateLang("en");
      await loadAll();
    } catch (error) {
      notify({
        type: "error",
        title: "Create failed",
        message: apiErrorMessage(error),
      });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (group) => {
    setEditingKey(group.groupKey);
    setEditForm(group.form);
    setEditAvatarFile(null);
    setActiveEditLang("en");
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    const courseGroup = resolveCourseGroup(editForm.courseKey);
    const courseId = courseGroup?.anchorId || editForm.primaryCourseId;
    if (!courseId) {
      return notify({ type: "error", title: "Course not found for this training" });
    }
    const titledLangs = LANGS.filter((lang) => (editForm.translations[lang].title || "").trim());
    if (!titledLangs.length) {
      return notify({ type: "error", title: "Training title is required in at least one language" });
    }

    setSaving(true);
    try {
      const payload = buildSavePayload(editForm);
      const saved = await saveAdminTrainingAllLanguages({ courseId, payload });
      const primary =
        (Array.isArray(saved) ? saved : []).find((r) => normLang(r.language) === "en") ||
        (Array.isArray(saved) ? saved[0] : null) ||
        { id: editForm.primaryId, courseId: editForm.primaryCourseId || courseId };

      if (editAvatarFile && primary?.id && primary?.courseId) {
        await uploadAdminTrainingTrainerAvatar({
          courseId: primary.courseId,
          trainingId: primary.id,
          file: editAvatarFile,
        });
      }
      notify({ type: "success", title: "Training updated for all languages" });
      closeEdit();
      await loadAll();
    } catch (error) {
      notify({
        type: "error",
        title: "Update failed",
        message: apiErrorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    if (!group?.primaryId || !group?.primaryCourseId) return;
    if (!window.confirm(`Delete training "${group.title}" in all languages?`)) return;
    try {
      await deleteAdminTraining({
        courseId: group.primaryCourseId,
        trainingId: group.primaryId,
        allLanguages: true,
      });
      notify({ type: "success", title: "Training deleted" });
      if (editingKey === group.groupKey) closeEdit();
      await loadAll();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: apiErrorMessage(error),
      });
    }
  };

  const handleRemoveAvatar = async () => {
    if (!editForm.primaryId || !editForm.primaryCourseId) return;
    try {
      await removeAdminTrainingTrainerAvatar({
        courseId: editForm.primaryCourseId,
        trainingId: editForm.primaryId,
      });
      setEditForm((p) => ({ ...p, trainerAvatarImageUrl: "" }));
      notify({ type: "success", title: "Avatar removed" });
      await loadAll();
    } catch (error) {
      notify({
        type: "error",
        title: "Remove avatar failed",
        message: apiErrorMessage(error),
      });
    }
  };

  const createNextOrder = nextDisplayOrderForCourse(createForm.courseKey);

  return (
    <section className="space-y-4">
      <div className="flex justify-end">
        <DashboardReloadButton onClick={loadAll} loading={loading} label="Reload list" />
      </div>

      <AdminCollapsibleSection
        title="Add new training"
        description="Pick a course, then fill English / Arabic / Kurdish. Title alone is enough per language — missing course languages are created automatically."
        badge="Create"
        footer={
          <button type="button" disabled={creating} onClick={handleCreate} className={btnAccent}>
            {creating ? "Saving..." : "Save training (all languages)"}
          </button>
        }
      >
        <div className="grid gap-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 sm:col-span-2">
              Course (shared Course Key)
              <select
                value={createForm.courseKey}
                onChange={(e) =>
                  setCreateForm((p) => ({
                    ...p,
                    courseKey: e.target.value,
                    displayOrder: nextDisplayOrderForCourse(e.target.value),
                  }))
                }
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              >
                {!courseGroups.length ? <option value="">No courses available</option> : null}
                {courseGroups.map((group) => (
                  <option key={group.key} value={group.courseKey}>
                    {group.title} ({group.courseKey}) —{" "}
                    {group.langs.map((l) => l.toUpperCase()).join("/") || "—"}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-slate-500">
                Same pattern as Boards: one training item can have EN / AR / KU tabs.
              </span>
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(createForm.isActive)}
                onChange={(e) => setCreateForm((p) => ({ ...p, isActive: e.target.checked }))}
              />
              <span>
                <span className="font-medium">Published on website</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Uncheck to hide this training from the public site (all languages).
                </span>
              </span>
            </label>
            <label className="text-sm text-slate-700">
              Display Order (shared across languages)
              <input
                type="number"
                value={createForm.displayOrder || createNextOrder}
                onChange={(e) =>
                  setCreateForm((p) => ({
                    ...p,
                    displayOrder: Number(e.target.value || createNextOrder),
                  }))
                }
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="text-sm text-slate-700 sm:col-span-2">
              Trainer Avatar File (shared)
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setCreateAvatarFile(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-sm"
              />
              <span className="mt-1 block text-xs text-slate-500">
                {createAvatarFile?.name
                  ? `Selected: ${createAvatarFile.name} — uploads when you save.`
                  : "Optional. Uploads after save and syncs to all languages."}
              </span>
            </label>
          </div>

          {renderLangTabs(activeCreateLang, setActiveCreateLang, createForm, "create")}
          {renderLangFields(createForm, setCreateForm, activeCreateLang)}
        </div>
      </AdminCollapsibleSection>

      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-[#011337]">Your trainings</h3>
          {!loading ? (
            <span className="text-sm text-slate-500">
              {groupedTrainings.length} item{groupedTrainings.length !== 1 ? "s" : ""}
              {courseGroups.length
                ? ` · ${courseGroups.length} course${courseGroups.length !== 1 ? "s" : ""}`
                : ""}
            </span>
          ) : null}
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-slate-200 bg-white">
            Loading trainings...
          </p>
        ) : groupedTrainings.length ? (
          <div className="grid gap-3">
            {groupedTrainings.map((group) => {
              const missing = LANGS.filter((lang) => !group.languages.includes(lang));
              return (
                <AdminProductListCard
                  key={group.groupKey}
                  id={group.primaryId}
                  title={group.title}
                  meta={[
                    group.courseTitle ? `Course: ${group.courseTitle}` : null,
                    group.courseKey ? `Key: ${group.courseKey}` : null,
                    `Order: ${group.displayOrder}`,
                    `Languages: ${group.languages.map((l) => l.toUpperCase()).join(", ") || "—"}`,
                    missing.length ? `Missing: ${missing.map((l) => l.toUpperCase()).join(", ")}` : null,
                  ].filter(Boolean)}
                  status={group.isActive}
                  imageUrl={group.avatar}
                  isEditing={editingKey === group.groupKey}
                  onEdit={() => startEdit(group)}
                  onDelete={() => handleDelete(group)}
                />
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
            No trainings found. Use &quot;Add new training&quot; above to create one.
          </p>
        )}
      </div>

      <AdminEditorModal
        open={Boolean(editingKey)}
        title={`Edit training: ${editForm.translations?.en?.title || editForm.courseKey || ""}`}
        subtitle="Edit every language, then save. Missing Arabic/Kurdish course rows are created automatically."
        onClose={closeEdit}
        footer={
          <>
            <button type="button" onClick={closeEdit} className={btnSecondary}>
              Cancel
            </button>
            <button type="button" onClick={saveEdit} disabled={saving} className={btnPrimary}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        }
      >
        <div className="grid gap-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-3">
              Trainer avatar (shared across languages)
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              {editForm.trainerAvatarImageUrl ? (
                <img
                  src={resolveMediaUrl(editForm.trainerAvatarImageUrl)}
                  alt=""
                  className="w-full sm:w-28 h-28 object-cover rounded-full border border-slate-200 bg-white"
                />
              ) : (
                <div className="w-full sm:w-28 h-28 rounded-full border border-dashed border-slate-300 bg-white flex items-center justify-center text-xs text-slate-400">
                  No avatar
                </div>
              )}
              <div className="flex-1 min-w-0 space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setEditAvatarFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm"
                />
                {editAvatarFile?.name ? (
                  <p className="text-xs text-slate-500">
                    Selected: {editAvatarFile.name} — uploads when you save changes.
                  </p>
                ) : (
                  <p className="text-xs text-slate-500">Choose a file to replace the current avatar.</p>
                )}
                <label className="block text-sm text-slate-700">
                  Avatar URL
                  <input
                    type="text"
                    value={editForm.trainerAvatarImageUrl}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, trainerAvatarImageUrl: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
                  />
                </label>
                {editForm.trainerAvatarImageUrl ? (
                  <button type="button" className={btnDanger} onClick={handleRemoveAvatar}>
                    Remove avatar
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700">
              Course Key
              <input
                type="text"
                value={editForm.courseKey}
                readOnly
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-slate-100 text-slate-500"
              />
            </label>
            <label className="text-sm text-slate-700">
              Display Order (shared across languages)
              <input
                type="number"
                value={editForm.displayOrder}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, displayOrder: Number(e.target.value || 1) }))
                }
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-slate-700 sm:col-span-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(editForm.isActive)}
                onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.checked }))}
              />
              <span>
                <span className="font-medium">Published on website</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Uncheck to hide this training from the public site (all languages).
                </span>
              </span>
            </label>
          </div>

          {renderLangTabs(activeEditLang, setActiveEditLang, editForm, "edit")}
          {renderLangFields(editForm, setEditForm, activeEditLang)}
        </div>
      </AdminEditorModal>
    </section>
  );
};

export default TrainingsManager;
